import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SecurityContext,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
// Store
import { Store } from '@ngrx/store';
import addDays from 'date-fns/add_days';
import dateFormat from 'date-fns/format';
import isWithinRange from 'date-fns/is_within_range';
import subMonths from 'date-fns/sub_months';
import { forEach as _forEach, get as _get, has as _has } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
// date-fns
import { env } from '@amfam/environment';
import { AnalyticsFacade, EventAnalyticParams } from '@amfam/shared/analytics';
import { BillAccount, BillingDocument, BillingPaymentPaths } from '@amfam/shared/models';
import { DocumentPipeService } from '@amfam/shared/ui/pipes';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { CopyService, openWindowBlob, WindowRef } from '@amfam/shared/utility/shared-services';
import { DsTableUtilService, LoadingSpinnerService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-bill-account-statement-history',
  templateUrl: './bill-account-statement-history.component.html',
  styleUrls: ['./bill-account-statement-history.component.scss'],
  standalone: false
})
export class BillAccountStatementHistoryComponent implements OnInit, OnChanges, OnDestroy {
  // filter the view showing only this bill account statements if billAccountNumber passed as input
  @Input() billAccountNumber: string;
  @Input() newBillingDesign = false;

  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<unknown>;
  @ViewChild('acctCellTpl', { static: true }) acctCellTpl: TemplateRef<unknown>;
  @ViewChild('dateCellTmpl', { static: true }) dateCellTmpl: TemplateRef<unknown>;
  @ViewChild('downloadCellTpl', { static: true }) downloadCellTpl: TemplateRef<unknown>;

  documentApi: string;

  // Table init vars
  cssClasses = 'material noshadow payment-statement-history';
  rows: {
    billAccountNumber: string;
    dateOnDocument: string;
    documentId: string;
    language: string;
    billAccountNickName: string;
  }[] = [];
  // eslint-disable-next-line @typescript-eslint/ban-types
  columns: Array<{}> = [];
  pageLimit = 10;
  footerHeight = '';

  public dataLoading: Observable<boolean>;
  statementHistoryLoaded$: Observable<boolean>;

  public historyList: BillingDocument[] = [];

  // TODO - this form name is inacurate
  billingPaymentsHistoryForm: UntypedFormGroup;
  statementHistoryForm;

  // Make the route constants available on the template
  billingPaths = BillingPaymentPaths;
  isContactBillingToggleEnabled = this.featureFlagService.isEnabled('contact_billing');

  statementHistoryList = [];
  filteredStatementHistory = [];
  riskDescriptionOption = ['All'];
  historyListLimit = 4;
  statemetHistorydisplayLimit = 0;
  statementHistoryCopy = this.copyService.getCopy('billing.statementHistory');

  private idToNicknameMap: Map<string, string> = new Map();
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private tableUtil: DsTableUtilService,
    private spinner: LoadingSpinnerService,
    private win: WindowRef,
    private sanitizer: DomSanitizer,
    private documentPipeService: DocumentPipeService,
    private deviceService: DeviceDetectorService,
    private featureFlagService: FeatureFlagService,
    protected analyticsFacade: AnalyticsFacade,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    //new form setup
    this.setupStatementForm();
    this.statemetHistorydisplayLimit = this.historyListLimit;

    this.setupForm();
    this.getColumns();

    this.dataLoading = this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading);
    this.statementHistoryLoaded$ = this.store.select(
      BillAccountsSelectors.selectBillAccountDocumentLoaded(this.billAccountNumber)
    );

    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.dataLoading,
      (billAccounts, dataLoading) => ({
        billAccounts: billAccounts,
        loading: dataLoading
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        // Early out if we're loading
        if (state.loading) {
          return;
        }

        this.historyList = [];

        _forEach(state.billAccounts, (billAccount: BillAccount) => {
          if (billAccount) {
            // Build a map of bill account numbers to nicknames
            this.idToNicknameMap.set(
              billAccount.billAccountNumber,
              _get(billAccount, 'billingPreferences.accountNickName', billAccount.billAccountNumber)
            );

            // Build out the history list. This will be filtered on later before building rows.
            if (_has(billAccount, 'billingDocumentsList')) {
              for (let i = 0; i < billAccount.billingDocumentsList.length; i++) {
                let document = billAccount.billingDocumentsList[i];
                document = Object.assign({}, document, {
                  billAccountNumber: billAccount.billAccountNumber
                });
                if (
                  billAccount.policyList?.length &&
                  billAccount.policyList[0].riskDescriptionList
                ) {
                  const riskDesc = billAccount.policyList[0].riskDescriptionList;
                  riskDesc.forEach(risk => {
                    if (!this.riskDescriptionOption.includes(risk)) {
                      this.riskDescriptionOption.push(risk);
                    }
                  });
                  this.historyList.push({ ...document, riskDescription: riskDesc });
                } else if (billAccount.policyTypeDisplayName) {
                  const riskDesc = billAccount.policyTypeDisplayName;
                  if (!this.riskDescriptionOption.includes(riskDesc)) {
                    this.riskDescriptionOption.push(riskDesc);
                  }
                  this.historyList.push({
                    ...document,
                    riskDescription: [billAccount.policyTypeDisplayName]
                  });
                } else {
                  this.historyList.push(document);
                }
              }
            }
          }
        });
        this.setStatementHistoryList(this.historyList);

        this.buildRowsList();

        if (this.rows && this.rows.length > this.pageLimit) {
          this.footerHeight = 'auto';
        } else {
          this.footerHeight = '';
        }
      });
  }

  ngOnChanges() {
    this.buildRowsList();
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  public openStatementOnAndroid(link: string) {
    let windowRef;
    if (!window.navigator) {
      windowRef = this.win.nativeWindow.open();
    }
    this.documentPipeService
      .retrieveDocument(link)
      .pipe(take(1))
      .subscribe(
        res => {
          if (!res) {
            if (windowRef) {
              windowRef.close();
            }
          }
          const url = this.sanitizer.sanitize(SecurityContext.URL, res);
          this.spinner.stop();
          openWindowBlob(this.win, url, res, windowRef, 'document.pdf');
        },
        () => {
          if (windowRef) {
            windowRef.close();
          }
        }
      );
  }

  public openStatement(link: string) {
    // GA Analytics for billing statement print confirmation
    const printConfEvent: EventAnalyticParams = {
      event: 'print_confirmation',
      method: 'Billing Statement'
    };
    this.analyticsFacade.trackEvent(printConfEvent);

    this.spinner.start();
    if (this.deviceService.device.toLowerCase() === 'android') this.openStatementOnAndroid(link);
    else {
      const windowRef = this.win.nativeWindow.open();
      this.documentPipeService
        .retrieveDocument(link)
        .pipe(take(1))
        .subscribe(
          res => {
            if (!res) {
              if (windowRef) {
                windowRef.close();
              }
            }
            const url = this.sanitizer.sanitize(SecurityContext.URL, res);
            this.spinner.stop();
            windowRef.location.href = url;
          },
          () => {
            if (windowRef) {
              windowRef.close();
            }
          }
        );
    }
  }

  setupStatementForm() {
    this.statementHistoryForm = this.formBuilder.group({
      monthRange: ['12', Validators.required],
      billAccount: ['All', Validators.required]
    });
  }

  setStatementHistoryList(statementHistory) {
    this.filterStatementHistoryList(statementHistory);
    this.statementHistoryList = this.filteredStatementHistory.slice(
      0,
      this.statemetHistorydisplayLimit
    );
  }

  filterStatementHistoryList(statementList) {
    statementList = this.sortStatemetHistoryList(statementList);

    let noOfMonth = this.statementHistoryForm.controls.monthRange.value;
    const billAcoountChange = this.statementHistoryForm.controls.billAccount.value;
    noOfMonth = Number(noOfMonth) + 1;

    const startDate = dateFormat(subMonths(new Date(), noOfMonth), 'YYYY-MM-DD');
    const endDate = dateFormat(addDays(new Date(), 7), 'YYYY-MM-DD');

    this.filteredStatementHistory = statementList.filter(statement => {
      const dateCheck = dateFormat(statement.dateOnDocument, 'YYYY-MM-DD');
      if (billAcoountChange === 'All') {
        return isWithinRange(dateCheck, startDate, endDate);
      } else {
        return (
          statement.riskDescription.includes(billAcoountChange) &&
          isWithinRange(dateCheck, startDate, endDate)
        );
      }
    });
  }

  sortStatemetHistoryList(statementHistory) {
    return statementHistory.sort(
      (statement1, statement2) =>
        new Date(statement2.dateOnDocument).getTime() -
        new Date(statement1.dateOnDocument).getTime()
    );
  }

  loadMore() {
    this.statemetHistorydisplayLimit += this.historyListLimit;
    this.setStatementHistoryList(this.historyList);
  }

  // AS : Function to build the columns needed to build datatable for the billing and payments history.
  private getColumns() {
    this.columns = [
      {
        cellTemplate: this.acctCellTpl,
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: true,
        name: 'Billing Account',
        shortName: 'Account',
        draggable: false,
        resizeable: false,
        sortable: true,
        canAutoResize: true,
        comparator: this.tableUtil.nameComparator.bind(this)
      },
      {
        cellTemplate: this.dateCellTmpl,
        headerTemplate: this.headerCellTpl,
        isHeaderOnMobile: true,
        name: 'Date',
        shortName: 'Date',
        draggable: false,
        resizable: false,
        sortable: true,
        canAutoResize: true,
        comparator: this.tableUtil.dateComparator.bind(this)
      },
      {
        cellTemplate: this.downloadCellTpl,
        headerTemplate: this.headerCellTpl,
        name: 'Download',
        shortName: 'Download',
        draggable: false,
        resizeable: false,
        sortable: false,
        canAutoResize: true
      }
    ];

    // remove the first column if rendering for a single bill account
    if (this.billAccountNumber) {
      this.columns.shift();
    }
  }

  // AS : Function to build the rows needed to build datatable for the billing and payments history.
  private buildRowsList() {
    // Clear the rows array
    this.rows = [];

    // Filter the list by bill account number if we have one
    let filteredList = this.historyList.filter(item => {
      if (item) {
        // If we have an input bill account number, make sure it matches before including it
        if (this.billAccountNumber) {
          return this.billAccountNumber === item.billAccountNumber;
        }
        // If we don't have an input bill account number, don't filter
        return true;
      }
    });

    if (this.billingPaymentsHistoryForm) {
      // Get the selected value from the form
      let selectedValue = this.billingPaymentsHistoryForm.controls['dateRangeMonths'].value;

      if (selectedValue === '12' || selectedValue === '24') {
        selectedValue = Number(selectedValue) + 1;
      }
      // Determine the earliest day to show using the selected value
      const startDate = dateFormat(subMonths(new Date(), selectedValue), 'YYYY-MM-DD');

      // Format todays date + 7 as we retrive the documents 7 days in the future.
      const endDate = dateFormat(addDays(new Date(), 7), 'YYYY-MM-DD');

      // Filter by the selected form value
      filteredList = filteredList.filter(item => {
        if (item) {
          // Format the dateOnDocument from api response to stable format.
          const dateCheck = dateFormat(item.dateOnDocument, 'YYYY-MM-DD');

          // SK: this condition also satisfies if dateCheck === startDate || endDate
          // isWithinRange also satisfies above conditions.
          return isWithinRange(dateCheck, startDate, endDate);
        }
      });
      const hasStatementInSpanish = !!filteredList.find(
        statement => (<string>statement.contentLanguage).toUpperCase() === 'SPANISH'
      );
      for (const historyItem of filteredList) {
        this.rows.push({
          billAccountNumber: historyItem.billAccountNumber,
          dateOnDocument: historyItem.dateOnDocument,
          documentId: historyItem.documentId,
          language:
            filteredList.length === 1 && historyItem.contentLanguage === 'ENGLISH'
              ? ''
              : hasStatementInSpanish && historyItem.contentLanguage === 'ENGLISH'
                ? '(English)'
                : this.getContentLanguage(historyItem.contentLanguage),
          billAccountNickName: this.idToNicknameMap.get(historyItem.billAccountNumber)
        });
      }

      this.rows.sort(
        (a, b) => this.tableUtil.dateComparator(a.dateOnDocument, b.dateOnDocument) * -1
      );
    }
  }

  private setupForm() {
    // AS: Building the Download link
    this.documentApi = env.documentApi;

    // Setup the form
    this.billingPaymentsHistoryForm = this.formBuilder.group({
      dateRangeMonths: ['12', Validators.compose([Validators.required])]
    });

    // Listen to changes in the form to filter the range
    this.billingPaymentsHistoryForm.valueChanges.pipe(takeUntil(this.stop$)).subscribe(() => {
      this.buildRowsList();
    });

    // Listen to changes in the form to filter the month range and billaccount
    this.statementHistoryForm.valueChanges.pipe(takeUntil(this.stop$)).subscribe(() => {
      this.statemetHistorydisplayLimit = this.historyListLimit;
      this.setStatementHistoryList(this.historyList);
    });
  }

  private getContentLanguage(contentLanguage: string): string {
    // Content language could be empty so check it before we set it
    if (contentLanguage) {
      return contentLanguage === 'ENGLISH' ? '' : '(Spanish)';
    } else {
      // If no language display none
      return '';
    }
  }
}
